/* Cloud sync for signed-in users. Each collection is a map of id → record with
   updatedAt (and deleted tombstones); both sides merge last-writer-wins per key,
   so order of arrival never loses a record. The server does the same merge
   (functions/api/sync.ts) and returns the union. */
import { useEffect, useRef } from 'react';
import { useProgress, useSettings } from './store';
import { useSession } from './auth';

const DEVICE_ONLY: (keyof ReturnType<typeof useSettings.getState>['settings'])[] = ['theme', 'glass'];

/* Set while applying a server response, so the merge itself doesn't count as
   a local change and trigger another sync (which would loop every 4s). */
let applying = false;

export async function syncNow(): Promise<boolean> {
  const p = useProgress.getState();
  const s = useSettings.getState();
  const settings = { ...s.settings } as Record<string, unknown>;
  DEVICE_ONLY.forEach((k) => delete settings[k]);
  const body = {
    sessions: p.sessions, goals: p.goals, prayers: p.prayers, adhkar: p.adhkar, bookmarks: p.bookmarks, lectureMarks: p.lectureMarks, fasts: p.fasts, khatmahs: p.khatmahs, ayahLog: p.ayahLog, dhikr: p.dhikr, hifz: p.hifz, hifzPages: p.hifzPages, hifzLog: p.hifzLog, hifzLessons: p.hifzLessons,
    // Health data: sent only when opted in; otherwise ask the server to forget any copy.
    ...(s.settings.syncPeriods ? { periods: p.periods } : { dropPeriods: true }),
    lastRead: p.lastRead,
    settings: { value: settings, updatedAt: s.settingsUpdatedAt },
  };
  const r = await fetch('/api/sync', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) return false;
  const remote = (await r.json()) as Parameters<ReturnType<typeof useProgress.getState>['mergeRemote']>[0] & { settings?: { value: Record<string, unknown>; updatedAt: number } };
  applying = true;
  try {
    useProgress.getState().mergeRemote(s.settings.syncPeriods ? remote : { ...remote, periods: undefined });
    if (remote.settings && remote.settings.updatedAt > s.settingsUpdatedAt) {
      useSettings.setState((st) => ({ settings: { ...st.settings, ...remote.settings!.value }, settingsUpdatedAt: remote.settings!.updatedAt }));
    }
  } finally { applying = false; }
  try { localStorage.setItem('ftu_last_sync', String(Date.now())); } catch {}
  return true;
}

/** Sync on sign-in, on focus, and (debounced) after local changes. */
export function useCloudSync() {
  const { data } = useSession();
  const signedIn = !!data?.user;
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!signedIn) return;
    void syncNow().catch(() => {});
    const onFocus = () => { if (!document.hidden) void syncNow().catch(() => {}); };
    document.addEventListener('visibilitychange', onFocus);
    const schedule = () => { if (applying) return; clearTimeout(timer.current); timer.current = setTimeout(() => void syncNow().catch(() => {}), 4000); };
    const u1 = useProgress.subscribe(schedule);
    const u2 = useSettings.subscribe(schedule);
    return () => { document.removeEventListener('visibilitychange', onFocus); u1(); u2(); clearTimeout(timer.current); };
  }, [signedIn]);
}
